import { hrefOf } from '../overview/href'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  softseeSummaryQuery,
  type DeviceSoftseeSummaryPage,
  type SoftseeOutdatedApp,
  type SoftseeSummaryTile,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Softsee Summary - softsee/devices/details/deviceSummary.php cu
 * gesturile din deviceSummary-1.js: cele opt casute (tonuri, numele
 * sistemului ca text, CVE pe Outdated), Main Applications, Outdated
 * Applications cu primele cinci vazute si butonul Show All.
 */

/**
 * CASUTA DIN TICHET E CASUTA DE PE FILA HEALTH, nu una "mai mica".
 * ---------------------------------------------------------------------------
 *
 * Pe fisa aparatului casutele stau in `.sum-tiles` (flex, `gap: 12px`), patru
 * pe rand, cu nota de doua randuri sub eticheta. In randul desfacut al
 * tichetului, panoul e jumatate de ecran (~900px), si fila Health de alaturi
 * deseneaza `col-md-2` + `.dashboard-stat` gol: 125 x 135px, cifra 34px, sase
 * pe rand. Utilizatoarea a cerut Software "ca la Health" - de trei ori.
 *
 * Primele doua incercari (c18d8b53f, 0ba99cdb6) au scris reguli peste
 * `.sum-tiles` in TicketDeviceTab.css: doua pe rand, cutia 120px, cifra 26px.
 * Masurat in Edge pe foile adevarate, casutele ieseau 444 x 120px si UNA pe
 * rand, nu doua: `.row::before` si `.row::after` (clearfix-ul Bootstrap) sunt
 * elemente flex in `.sum-tiles`, fiecare cu `gap`-ul lui de 12px, deci prima
 * casuta de pe rand nu mai incapea langa a doua. Nicio regula de font nu putea
 * repara asta.
 *
 * Deci in tichet (`from === 'easeedesk'`) nu se mai potriveste `.sum-tiles`, ci
 * se scrie EXACT marcajul lui `DeviceOpteemumSummary.Tile`: `col-md-2`,
 * `.dashboard-stat`, `.number.number-summary` (textul cu `font-24`, 16/31px
 * inline, ca Role/Status de pe Health), `.desc`, banda `more`. Nota nu incape
 * intr-o cutie de 125px si trece in `title`, ca sa nu se piarda de tot.
 */
function Tile({ tile, id, open, compact }: { tile: SoftseeSummaryTile; id: number; open(href: string): void; compact: boolean }) {
  const noteText = tile.note.replace(/<br\s*\/?>/gi, ' · ').replace(/<[^>]+>/g, '').trim()
  const title = [tile.title, compact ? noteText : ''].filter((t) => t !== '').join(' — ')
  const more =
    tile.more.href !== '' ? (
      <a
        className="more action_button"
        href={hrefOf(`${tile.more.href}${id}`) ?? '#'}
        onClick={(ev) => {
          ev.preventDefault()
          open(tile.more.href)
        }}
      >
        {tile.more.label} <i className="m-icon-swapright m-icon-white"></i>
      </a>
    ) : (
      <div className="more">{tile.more.label}</div>
    )

  if (compact) {
    return (
      <div className="col-md-2">
        <div className={`dashboard-stat ${tile.tone}`}>
          <div className="visual">
            <i className={tile.icon}></i>
          </div>
          <div className="details" title={title !== '' ? title : undefined}>
            {/*
              Numele sistemului e lung ("Microsoft Windows 11 Pro") si cutia are ~95px de
              text: 16px, randul strans la 18px si cel mult trei randuri (79 = 25 padding +
              3 x 18); numele intreg sta in `title`. FARA `font-24`: in app.css e
              `24px !important` si ar bate inline-ul (pe Health chiar il bate - Role/Status
              ies la 24px, dar acolo sunt cuvinte scurte).
            */}
            {tile.text ? (
              <div
                className="number number-summary"
                style={{ fontSize: 16, lineHeight: '18px', maxHeight: 79, overflow: 'hidden' }}
              >
                {tile.number !== '' ? tile.number : <>&ndash;</>}
              </div>
            ) : (
              <div className="number number-summary">{tile.number}</div>
            )}
            <div className="desc">{tile.label}</div>
          </div>
          {more}
        </div>
      </div>
    )
  }

  return (
    <div className="sum-tile">
      <div className={`dashboard-stat ${tile.tone}`}>
        <div className="visual">
          <i className={tile.icon}></i>
        </div>
        <div className="details" title={title !== '' ? title : undefined}>
          {tile.text ? (
            <div className="number sum-text">{tile.number !== '' ? tile.number : <>&ndash;</>}</div>
          ) : (
            <div className="number number-summary">{tile.number}</div>
          )}
          <div className="desc">{tile.label}</div>
          {tile.note.trim() !== '' && <div className="sum-note" dangerouslySetInnerHTML={{ __html: tile.note }} />}
        </div>
        {more}
      </div>
    </div>
  )
}

function Applications({ page }: { page: DeviceSoftseeSummaryPage }) {
  const apps = page.applications
  return (
    <div className="row row-content">
      <div className="col-md-12 divider">
        <h4 className="bordered">Main Applications</h4>
      </div>
      <div className="col-md-12">
        <table className="mlist-details" width="100%">
          <tbody>
            {apps ? (
              <>
                <tr>
                  <td width="150">
                    <b>Operating system: </b>
                  </td>
                  <td>{apps.operating_system}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Office: </b>
                  </td>
                  <td>{apps.office}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Email client: </b>
                  </td>
                  <td>{apps.default_mail_client}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Default browser: </b>
                  </td>
                  <td>{apps.default_browser}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Antivirus: </b>
                  </td>
                  <td>{apps.antivirus}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Firewall: </b>
                  </td>
                  <td>{apps.firewall}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Antispyware: </b>
                  </td>
                  <td>{apps.antispyware}</td>
                </tr>
                <tr style={{ backgroundColor: 'rgb(176, 217, 248)' }}>
                  <td width="150">
                    <b>OS available updates: </b>
                  </td>
                  <td>{apps.os_available_updates}</td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={7} className="bg-transparent">
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle"></i> No results found.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Cautarea din linia titlului: filtreaza pe loc lista primita (vine intreaga
 * de la capat, fara paginare), dupa nume, versiune si ultima versiune, fara
 * sa tina cont de litere mari. Cat timp se cauta, se vad toate potrivirile -
 * pragul de cinci randuri si Show All sunt pentru lista intreaga.
 */
function matchesOutdated(app: SoftseeOutdatedApp, term: string): boolean {
  const q = term.trim().toLowerCase()
  if (q === '') return true
  return [app.application_name, app.version, app.last_version].some((v) => String(v ?? '').toLowerCase().includes(q))
}

function Outdated({ page }: { page: DeviceSoftseeSummaryPage }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const searching = search.trim() !== ''
  const rows = searching ? page.outdated.filter((app) => matchesOutdated(app, search)) : page.outdated

  return (
    <div className="row row-content">
      <div className="col-md-12 divider">
        <h4 className="bordered" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>Outdated Applications</span>
          {page.outdated.length > 0 && (
            <input
              type="search"
              className="form-control input-sm"
              placeholder="Search..."
              aria-label="Search outdated applications"
              autoComplete="off"
              style={{ width: 220, maxWidth: '50%', fontSize: 12, fontFamily: 'inherit' }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </h4>
      </div>
      <div className="col-md-12">
        <table className="table table-striped centered" width="100%">
          <thead>
            <tr>
              <th>#</th>
              <th className="a-left">Application name</th>
              <th>Version</th>
              <th>Last version</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((app, i) => {
                const n = i + 1
                const hidden = !searching && n > 5
                return (
                  <tr
                    key={`${app.application_name}-${n}`}
                    style={hidden && !open ? { display: 'none' } : undefined}
                    className={hidden ? 'hidden-outdated' : undefined}
                  >
                    <td>#{n}</td>
                    <td className="a-left">
                      <b>{app.application_name} </b>
                    </td>
                    <td>
                      <span className="badge bg-warning">{app.version}</span>
                    </td>
                    <td>
                      <span className="badge bg-green2">{app.last_version}</span>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={4}>{searching ? 'No matching applications.' : 'No results found.'}</td>
              </tr>
            )}
          </tbody>
        </table>
        {!searching && rows.length > 5 && (
          <button className="btn btn-sm btn-danger show-all-outdated" type="button" onClick={() => setOpen((v) => !v)}>
            {open ? (
              <>
                Hide All Outdated... <i className="far fa-arrow-alt-circle-up"></i>
              </>
            ) : (
              <>
                Show All Outdated... <i className="far fa-arrow-alt-circle-down"></i>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export function DeviceSoftseeSummary({ id, from, open }: ScreenProps) {
  const { data, error } = useQuery(softseeSummaryQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  /* In tichet, un singur `.row` de `col-md-2`, ca pe fila Health de alaturi;
     opt casute se rup 6 + 2, cum le rupe Bootstrap. Vezi `Tile`. */
  const compact = from === 'easeedesk'

  return (
    <>
      {compact ? (
        <div className="row">
          {data.tiles.map((t, i) => (
            <Tile key={i} tile={t} id={id} open={open} compact />
          ))}
        </div>
      ) : (
        <>
          <div className="row sum-tiles">
            {data.tiles.slice(0, 4).map((t, i) => (
              <Tile key={i} tile={t} id={id} open={open} compact={false} />
            ))}
          </div>
          <div className="row sum-tiles" style={{ marginTop: 12 }}>
            {data.tiles.slice(4).map((t, i) => (
              <Tile key={i} tile={t} id={id} open={open} compact={false} />
            ))}
          </div>
        </>
      )}
      <Applications page={data} />
      <Outdated page={data} />
    </>
  )
}
